import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '../components/page-action-bar/page-action-bar';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { ISupplier } from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';

const SLUG_RX = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Combined create + edit page for a single Supplier. Mirrors the
// stock-edit / menu-edit pattern (init pattern with _initStarted guard
// so F5 doesn't latch "no active shop").
//
// Create mode: path = /suppliers/new
// Edit mode:   path = /suppliers/:id
@customElement('supplier-edit-page')
export class SupplierEditPage extends Base {
  @state() private _loading = true;
  @state() private _busy = false;
  @state() private _saved = false;
  @state() private _error: string | null = null;
  @state() private _existing: ISupplier | null = null;

  @state() private _id = '';
  @state() private _name = '';
  @state() private _contactName = '';
  @state() private _contactEmail = '';
  @state() private _contactPhone = '';
  @state() private _notes = '';
  @state() private _isActive = true;

  private _initStarted = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 560px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      @media (max-width: 720px) {
        :host {
          padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
        }
      }
      form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .actions {
        display: flex;
        gap: 8px;
        align-items: center;
        margin-top: 8px;
      }
      .danger-zone {
        margin-top: 16px;
        padding: 12px;
        border: 1px dashed var(--sl-color-danger-300);
        border-radius: var(--sl-border-radius-medium);
      }
    `,
  ];

  firstUpdated() {
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;

    if (!this.activeShop) {
      this._error = 'No active shop. Pick one first.';
      this._loading = false;
      return;
    }
    if (!this.userProfile?.hasRole(this.activeShop.shopName, 'owner')) {
      this._error = 'Only the shop owner can manage suppliers.';
      this._loading = false;
      return;
    }

    const editId = readEditIdFromPath();
    if (editId) {
      const api = ApiFactory.authenticated();
      const r = await api.getSupplier(this.activeShop.shopName, editId);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Supplier not found.';
        this._loading = false;
        return;
      }
      this._existing = r.data;
      this._id = r.data.id;
      this._name = r.data.name;
      this._contactName = r.data.contactName ?? '';
      this._contactEmail = r.data.contactEmail ?? '';
      this._contactPhone = r.data.contactPhone ?? '';
      this._notes = r.data.notes ?? '';
      this._isActive = r.data.isActive;
    }
    this._loading = false;
  }

  private get _isEdit(): boolean {
    return this._existing !== null;
  }

  private async _save(e: Event) {
    e.preventDefault();
    if (this._busy || !this.activeShop) return;
    const name = this._name.trim();
    if (!name) {
      this._error = 'Name is required.';
      return;
    }
    this._busy = true;
    this._error = null;
    this._saved = false;
    try {
      const api = ApiFactory.authenticated();
      if (this._isEdit) {
        const r = await api.updateSupplier(this.activeShop.shopName, this._id, {
          name,
          contactName: this._contactName.trim() || null,
          contactEmail: this._contactEmail.trim() || null,
          contactPhone: this._contactPhone.trim() || null,
          notes: this._notes.trim() || null,
          isActive: this._isActive,
        });
        if (!r.success || !r.data) {
          this._error = r.error ?? 'Save failed.';
          return;
        }
        this._existing = r.data;
        this._saved = true;
      } else {
        const id = this._id.trim().toLowerCase();
        if (!id || !SLUG_RX.test(id)) {
          this._error =
            'Id must be 2-48 chars, lowercase letters/digits/hyphens, no leading/trailing hyphen.';
          return;
        }
        const r = await api.createSupplier(this.activeShop.shopName, {
          id,
          name,
          contactName: this._contactName.trim() || undefined,
          contactEmail: this._contactEmail.trim() || undefined,
          contactPhone: this._contactPhone.trim() || undefined,
          notes: this._notes.trim() || undefined,
        });
        if (!r.success || !r.data) {
          this._error = r.error ?? 'Create failed.';
          return;
        }
        router.navigate(resolveRouterPath('suppliers'));
      }
    } catch (err) {
      this._error = (err as Error)?.message ?? 'Unexpected error.';
    } finally {
      this._busy = false;
    }
  }

  private async _deactivate() {
    if (!this._existing || !this.activeShop) return;
    if (
      !confirm(
        `Deactivate "${this._existing.name}"? Existing POs and deliveries keep their reference. You can reactivate later.`
      )
    )
      return;
    this._busy = true;
    try {
      const r = await ApiFactory.authenticated().deactivateSupplier(
        this.activeShop.shopName,
        this._existing.id
      );
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Deactivate failed.';
        return;
      }
      this._existing = r.data;
      this._isActive = r.data.isActive;
      this._saved = true;
    } finally {
      this._busy = false;
    }
  }

  private _back() {
    router.navigate(resolveRouterPath('suppliers'));
  }

  render() {
    if (this._loading) return html`<sl-card><p>Loading…</p></sl-card>`;
    if (this._error && !this._existing && !this._id) {
      return html`<sl-alert variant="danger" open>${this._error}</sl-alert>`;
    }

    return html`
      <sl-card>
        <div
          slot="header"
          style="display: flex; align-items: center; gap: 8px;"
        >
          <h2 style="margin: 0; flex: 1;">
            ${this._isEdit ? this._existing?.name : 'New supplier'}
          </h2>
          ${
            this._isEdit && !this._isActive
              ? html`<sl-badge variant="neutral" pill>inactive</sl-badge>`
              : nothing
          }
        </div>

        <form @submit=${this._save}>
          <sl-input
            label="Id (slug)"
            help-text="Lowercase, hyphens. Cannot be changed later."
            .value=${this._id}
            ?disabled=${this._isEdit}
            @sl-input=${(e: Event) =>
              (this._id = (e.target as HTMLInputElement).value
                .toLowerCase()
                .trim())}
            required
          ></sl-input>

          <sl-input
            label="Name"
            help-text="What the supplier is known as in this shop."
            .value=${this._name}
            @sl-input=${(e: Event) => (this._name = (e.target as HTMLInputElement).value)}
            required
          ></sl-input>

          <sl-input
            label="Contact name (optional)"
            .value=${this._contactName}
            @sl-input=${(e: Event) => (this._contactName = (e.target as HTMLInputElement).value)}
          ></sl-input>

          <sl-input
            type="email"
            label="Contact email (optional)"
            .value=${this._contactEmail}
            @sl-input=${(e: Event) => (this._contactEmail = (e.target as HTMLInputElement).value)}
          ></sl-input>

          <sl-input
            label="Contact phone (optional)"
            .value=${this._contactPhone}
            @sl-input=${(e: Event) => (this._contactPhone = (e.target as HTMLInputElement).value)}
          ></sl-input>

          <sl-textarea
            label="Notes (optional)"
            help-text="Anything you want to remember about this supplier — terms, delivery days, contact preferences."
            rows="3"
            .value=${this._notes}
            @sl-input=${(e: Event) => (this._notes = (e.target as HTMLInputElement).value)}
          ></sl-textarea>

          ${
            this._isEdit
              ? html`
                  <label
                    style="display: flex; align-items: center; gap: 8px; cursor: pointer;"
                  >
                    <sl-switch
                      ?checked=${this._isActive}
                      @sl-change=${(e: Event) => {
                        this._isActive = (e.target as HTMLInputElement).checked;
                        this._saved = false;
                      }}
                    ></sl-switch>
                    <span
                      ><strong>Active</strong> — uncheck to hide from PO +
                      delivery pickers without losing history.</span
                    >
                  </label>
                `
              : nothing
          }
          ${
            this._error
              ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>`
              : nothing
          }
          ${
            this._saved
              ? html`<sl-alert variant="success" open closable
                  >Saved.</sl-alert
                >`
              : nothing
          }

          <page-action-bar>
            <sl-button
              slot="left"
              variant="default"
              @click=${this._back}
              ?disabled=${this._busy}
            >
              Back to list
            </sl-button>
            <sl-button
              slot="right"
              type="submit"
              variant="primary"
              ?loading=${this._busy}
              ?disabled=${this._busy}
            >
              ${this._isEdit ? 'Save changes' : 'Create supplier'}
            </sl-button>
          </page-action-bar>
        </form>

        ${
          this._isEdit && this._existing?.isActive
            ? html`
                <div class="danger-zone">
                  <strong>Deactivate</strong>
                  <p style="margin: 4px 0 8px 0; opacity: 0.75;">
                    Hides this supplier from new PO + delivery pickers. Existing
                    POs + deliveries keep their reference and continue to read
                    the supplier's name normally. You can reactivate later.
                  </p>
                  <sl-button
                    variant="warning"
                    @click=${this._deactivate}
                    ?disabled=${this._busy}
                  >
                    Deactivate
                  </sl-button>
                </div>
              `
            : nothing
        }
      </sl-card>
    `;
  }
}

function readEditIdFromPath(): string | null {
  const m = window.location.pathname.match(/\/suppliers\/([^/?#]+)/);
  if (!m) return null;
  if (m[1] === 'new') return null;
  return decodeURIComponent(m[1]);
}
